import { HttpHeaders } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { AcceptedServicesState, HyundaiMenuNgrxFacade } from '@quorum/hyundai-menu-ngrx';
import {
  AppointmentCase,
  AuthenticatedUser,
  Employee,
  ServiceAppointmentDeclinedMpviCode,
  ServiceGroup,
  Vehicle,
} from '@quorum/models/xs-resource';
import { OemIntegrationService, OemMenuService } from '@quorum/quorum-api';
import { DeclinedReasonWithDefault } from '@quorum/service-lane-dropdowns';
import { ServiceLaneDropdownsFacade } from '@quorum/service-lane-dropdowns/facade';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import {
  AppointmentCasesEntityActionTypes,
  QuoteLinesEntityActionTypes,
  ServiceAppointmentDeclinedOperationsEntityActionTypes,
  WorkOrderCasesEntityActionTypes,
  WorkOrderDeclinedOperationsEntityActionTypes,
} from '@quorum/sha-entities/fixed-operations';
import { ServiceLaneActionTypes } from 'libs/service-lane-root/src/lib/+state/service-lane.actions';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'sha-hyundai-menu',
  templateUrl: './hyundai-menu.component.html',
  styleUrls: ['./hyundai-menu.component.scss'],
})
export class HyundaiMenuComponent implements OnInit, OnDestroy {
  menuData$: Observable<any>;
  authenticatedEmployee$: Observable<Employee>;
  declinedReasonsDropdown$: Observable<DeclinedReasonWithDefault[]>;
  defaultDeclinedReason: DeclinedReasonWithDefault;
  previousDefaultDeclinedReason: DeclinedReasonWithDefault;
  currentDefaultDeclinedReason: DeclinedReasonWithDefault;
  nextDefaultDeclinedReason: DeclinedReasonWithDefault;
  actionsSubscription$: Subscription;
  showFullSchedule = false;

  constructor(
    private oemIntegrationService: OemIntegrationService,
    private oemMenuService: OemMenuService,
    public serviceLaneFacade: ServiceLaneFacade,
    private activatedRoute: ActivatedRoute,
    public serviceLaneDropdownsFacade: ServiceLaneDropdownsFacade,
    private authenticationStateService: AuthenticationStateService,
    public actions$: Actions,
    private apiService: ApiService,
    public hyundaiMenuNgrxFacade: HyundaiMenuNgrxFacade
  ) {}

  ngOnDestroy(): void {
    this.actionsSubscription$.unsubscribe();
  }

  ngOnInit(): void {
    this.menuData$ = combineLatest([
      this.activatedRoute.queryParams,
      this.authenticationStateService.selectAuthenticatedUser(),
    ]).pipe(
      map(([routerParams, authenticatedUser]) => {
        return { routerParams, authenticatedUser };
      }),
      switchMap((data: { routerParams: Params; authenticatedUser: AuthenticatedUser }) => {
        if (data.routerParams.transactionId) {
          return this.apiService.get<Vehicle>(`v/1/vehicles/vehicles/${data.routerParams.vehicleId}`).pipe(
            map((vehicle) => {
              return { odometer: data.routerParams.odometer, vehicle, authenticatedUser: data.authenticatedUser };
            })
          );
        } else {
          return combineLatest([
            this.serviceLaneFacade.selectedAppointment$.pipe(filter((appointment) => appointment != null)),
            this.serviceLaneFacade.vehicle$.pipe(filter((vehicle) => vehicle != null)),
          ]).pipe(
            map(([appointment, vehicle]) => {
              return {
                odometer: appointment.odometerIn ? appointment.odometerIn : vehicle.currentMileage,
                vehicle,
                authenticatedUser: data.authenticatedUser,
              };
            })
          );
        }
      }),
      switchMap((data) => {
        const params = {
          odometer: data.odometer,
          vehicleVin: data.vehicle.vin,
          vehicleYear: data.vehicle.year,
        };

        this.oemMenuService.initialize(data.authenticatedUser.user.destination);

        return this.oemMenuService.businessId$.pipe(
          filter((businessId) => businessId != null),
          switchMap((businessId) => {
            let headers: HttpHeaders = new HttpHeaders();
            headers = headers.append('client_api_key', 'C53jiaePs0XuCN1dwYn+TMrtmZkgZLyhODuI8H3OubI=');
            headers = headers.append('client_app_id', 'f0dde5a9-3082-4ea9-b60a-1c7967808eff');

            return this.oemIntegrationService
              .get(`hyundai/vehicles/vin-details/en/HYN/${params.vehicleVin}`, {
                headers: headers,
              })
              .pipe(
                switchMap((vinDetails) => {
                  const age: number = this.monthDiff(new Date(vinDetails.WarrantyStartDate), new Date());
                  return combineLatest([
                    this.oemMenuService.get(
                      `business/${businessId}/vehicle-schedule-with-pricing/en/vin/${params.vehicleVin}/${params.vehicleYear}/${params.odometer}`
                    ),
                    this.oemIntegrationService.get(
                      `/hyundai/vehicles-service-schedule/full-maintenance-schedule/${'EN'}/${'HYN'}/${
                        params.vehicleVin
                      }/${params.vehicleYear}/${
                        params.odometer == 0 ? `${params.odometer}/${age}` : `${params.odometer}`
                      }`,
                      { headers: headers }
                    ),
                  ]).pipe(
                    map(([hyundaiResponse, fullScheduleResponse]) => {
                      return { hyundaiResponse, fullScheduleResponse, vehicle: data.vehicle };
                    })
                  );
                })
              );
          })
        );
      }),
      catchError((error: any) => {
        return of({ hyundaiResponse: { error: 'Vehicle schedule data could not be found.' } });
      })
    );

    this.authenticatedEmployee$ = this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(filter((authEmployee) => authEmployee != null));

    this.declinedReasonsDropdown$ = this.serviceLaneDropdownsFacade.declinedReasonsDropdown$.pipe(
      tap((declinedReasons) => {
        this.defaultDeclinedReason = declinedReasons.find((declinedReason) => declinedReason.isDefault == true);
        this.previousDefaultDeclinedReason = declinedReasons.find((declinedReason) => declinedReason.isDefault == true);
        this.currentDefaultDeclinedReason = declinedReasons.find((declinedReason) => declinedReason.isDefault == true);
        this.nextDefaultDeclinedReason = declinedReasons.find((declinedReason) => declinedReason.isDefault == true);
      })
    );

    this.actionsSubscription$ = this.actions$
      .pipe(
        ofType(
          ServiceLaneActionTypes.UpdateApprovedQuoteLineInState,
          QuoteLinesEntityActionTypes.DeleteQuoteLineFromServerSuccess,
          AppointmentCasesEntityActionTypes.CreateAppointmentCaseOnServerSuccess,
          AppointmentCasesEntityActionTypes.DeleteAppointmentCaseOnServerSuccess,
          ServiceAppointmentDeclinedOperationsEntityActionTypes.CreateServiceAppointmentDeclinedOperationsOnServerSuccess,
          ServiceAppointmentDeclinedOperationsEntityActionTypes.DeleteSelectedAppointmentDeclinedMpviFromServerSuccess,
          WorkOrderDeclinedOperationsEntityActionTypes.CreateWorkOrderDeclinedOperationsOnServerSuccess,
          WorkOrderDeclinedOperationsEntityActionTypes.DeleteWorkOrderDeclinedOperationFromServerSuccess,
          WorkOrderCasesEntityActionTypes.CreateWorkOrderCaseOnServerSuccess,
          WorkOrderCasesEntityActionTypes.DeleteWorkOrderCaseFromServerSuccess,
          QuoteLinesEntityActionTypes.CreateQuoteLineOnServerSuccess,
          QuoteLinesEntityActionTypes.UpdateQuoteLineOnServerSuccess
        ),
        map((action: any) => {
          switch (action.type) {
            case ServiceLaneActionTypes.UpdateApprovedQuoteLineInState:
            case AppointmentCasesEntityActionTypes.CreateAppointmentCaseOnServerSuccess:
            case WorkOrderCasesEntityActionTypes.CreateWorkOrderCaseOnServerSuccess:
            case ServiceAppointmentDeclinedOperationsEntityActionTypes.CreateServiceAppointmentDeclinedOperationsOnServerSuccess:
            case WorkOrderDeclinedOperationsEntityActionTypes.CreateWorkOrderDeclinedOperationsOnServerSuccess:
            case QuoteLinesEntityActionTypes.CreateQuoteLineOnServerSuccess:
            case QuoteLinesEntityActionTypes.UpdateQuoteLineOnServerSuccess:
              this.serviceLaneFacade.disableHyundaiMenuRemoveServiceButton$.next(false);
              break;
            case QuoteLinesEntityActionTypes.DeleteQuoteLineFromServerSuccess:
            case AppointmentCasesEntityActionTypes.DeleteAppointmentCaseOnServerSuccess:
            case WorkOrderCasesEntityActionTypes.DeleteWorkOrderCaseFromServerSuccess:
            case ServiceAppointmentDeclinedOperationsEntityActionTypes.DeleteSelectedAppointmentDeclinedMpviFromServerSuccess:
            case WorkOrderDeclinedOperationsEntityActionTypes.DeleteWorkOrderDeclinedOperationFromServerSuccess:
              this.serviceLaneFacade.disableHyundaiMenuAddServiceButton$.next(false);
              break;
            default:
              break;
          }
        })
      )
      .subscribe();
  }

  monthDiff(d1: Date, d2: Date) {
    let months;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();
    return months <= 0 ? 0 : months;
  }

  checkServiceGroupStatus(status: string, serviceGroup: ServiceGroup, hyundaiServices: any) {
    let statusArray: any = [];

    switch (status) {
      case 'accepted':
        statusArray = hyundaiServices?.acceptedServices.ids;
        break;
      case 'declined':
        statusArray = hyundaiServices?.declinedServices.ids;
        break;
    }

    // declined ops use opCode instead of service group id so have to check both, rare chance they could be the same?
    if (statusArray.includes(serviceGroup?.id) || statusArray.includes(serviceGroup?.workOrderLocationOpCode)) {
      return true;
    } else {
      return false;
    }
  }

  acceptService(service: any, vehicle: Vehicle) {
    this.hyundaiMenuNgrxFacade.acceptServiceForHyundaiMenu({
      id: service.dmsOpCodeRecord.id,
      status: null,
    });
    this.serviceLaneFacade.acceptServiceFromHyundaiMenu$.next({
      serviceGroup: service.dmsOpCodeRecord,
      vehicle: vehicle,
    });
  }

  removeAcceptedService(service: any) {
    this.serviceLaneFacade.disableHyundaiMenuAddServiceButton$.next(true);
    this.hyundaiMenuNgrxFacade.removeAcceptedServiceFromHyundaiMenu(service.dmsOpCodeRecord.id);
    this.serviceLaneFacade.removeAcceptedServiceFromHyundaiMenu$.next(service.dmsOpCodeRecord.id);
    this.serviceLaneFacade.acceptServiceFromHyundaiMenu$.next(null);
  }

  declineService(service: any) {
    this.serviceLaneFacade.declineServiceFromHyundaiMenu$.next({
      serviceGroup: service.dmsOpCodeRecord,
      declinedReason: this.defaultDeclinedReason.id,
    });
  }

  removeDeclinedService(service: any) {
    this.serviceLaneFacade.disableHyundaiMenuAddServiceButton$.next(true);
    this.serviceLaneFacade.declineServiceFromHyundaiMenu$.next({ serviceGroup: null, declinedReason: null });
    this.serviceLaneFacade.removeDeclinedServiceFromHyundaiMenu$.next(service.dmsOpCodeRecord);
  }

  declinedReasonSelected(service: any, declinedReason: DeclinedReasonWithDefault) {
    this.serviceLaneFacade.updateDeclinedServiceFromHyundaiMenu$.next({
      serviceGroup: service.dmsOpCodeRecord,
      declinedReason: declinedReason,
    });
    this.hyundaiMenuNgrxFacade.declineServiceForHyundaiMenu({
      id: service.dmsOpCodeRecord.workOrderLocationOpCode,
      declinedReasonId: declinedReason,
    });
  }

  acceptedAsALineItem(
    serviceGroup: ServiceGroup,
    selectedAppointmentCases?: {
      apptCase: AppointmentCase;
      serviceGroup: ServiceGroup;
    }[]
  ) {
    let lineItemArray: string[] = [];

    if (selectedAppointmentCases) {
      lineItemArray = selectedAppointmentCases.map(
        (apptCaseData: { apptCase: AppointmentCase; serviceGroup: ServiceGroup }) =>
          apptCaseData.apptCase.serviceGroupId
      );
    }

    if (lineItemArray.includes(serviceGroup?.id)) {
      return true;
    } else {
      return false;
    }
  }

  declinedOnAppointment(
    serviceGroup: ServiceGroup,
    selectedAppointmentDeclinedOperations?: ServiceAppointmentDeclinedMpviCode[]
  ) {
    let declinedArray: string[] = [];

    if (selectedAppointmentDeclinedOperations) {
      declinedArray = selectedAppointmentDeclinedOperations.map(
        (declinedOperation: ServiceAppointmentDeclinedMpviCode) => declinedOperation.opCode
      );
    }

    if (declinedArray.includes(serviceGroup?.workOrderLocationOpCode)) {
      return true;
    } else {
      return false;
    }
  }

  alreadyProcessedOnWorkOrder(serviceGroup: ServiceGroup, acceptedServices: AcceptedServicesState) {
    const acceptedService = acceptedServices.entities[serviceGroup?.id];
    if (acceptedService) {
      if (acceptedService.status !== null) {
        return true;
      } else {
        return false;
      }
    }
  }
}
